import { useEffect, useMemo, useRef } from 'react';
import { useQuickFillStore } from '../../../shared/stores/useQuickFillStore';
import { useWorkflowMode } from '../../../shared/stores/useParameterWorkflowStore';
import {
  useConfigurationStore,
  useConnectorBindingConfiguration,
  useMenuConfiguration
} from '../../../shared/stores/useConfigurationStore';
import { normalizeRevitParameterName, sanitizeRevitParameterNames } from '../../../shared/lib/revitParameterNames';
import type {
  ConnectorBindingConfiguration,
  MappingConfiguration,
  MenuConfiguration,
  QuickFillProperty,
} from '../../../shared/types';

const NON_QUICKFILL_MAPPING_VALUES = new Set(['BoltBased', 'GasketBased']);
const REQUIRED_WORKFLOW_PROPERTY_IDS = new Set(['skey', 'object-type', 'piping-spec']);

const createMappedQuickFillColumnId = (parameterName: string): string =>
  `extra-mapped-${parameterName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

function splitRevitParameterTokens(parameterName: string): string[] {
  return parameterName
    .split(/\s+/)
    .map((token) => token.trim())
    .filter((token) => token.length > 0);
}

const getQuickFillBaseParameterNames = (properties: QuickFillProperty[]): Set<string> =>
  new Set(
    sanitizeRevitParameterNames(
      properties
        .map((property) => property?.revitParameter)
        .filter((value): value is string => typeof value === 'string')
    ).map(normalizeRevitParameterName)
  );

function collectKnownParameterNames(
  properties: QuickFillProperty[],
  extraProperties?: QuickFillProperty[]
): Set<string> {
  const names = getQuickFillBaseParameterNames(properties);
  (extraProperties ?? []).forEach((property) => {
    if (property?.revitParameter) {
      names.add(normalizeRevitParameterName(property.revitParameter));
    }
  });
  return names;
}

export function collectMappedQuickFillColumns(
  mappingConfiguration: MappingConfiguration | undefined,
  properties: QuickFillProperty[],
  extraProperties?: QuickFillProperty[]
): QuickFillProperty[] {
  if (!mappingConfiguration) {
    return [];
  }

  const mappedObjectTypes = mappingConfiguration.general?.mappingTable?.objectType;
  if (!Array.isArray(mappedObjectTypes)) {
    return [];
  }

  const knownParameterNames = collectKnownParameterNames(properties, extraProperties);
  const mappedParameterNames = new Set<string>();

  mappedObjectTypes.forEach((objectType) => {
    objectType.attributes.forEach((attribute) => {
      if (attribute.attributeType !== 'Parameter' && attribute.attributeType !== 'BomColumn') {
        return;
      }

      if (attribute.pcfAttributeName?.trim().toUpperCase() === 'BOLT-MODELING') {
        return;
      }

      const rawParameterName = attribute.revitParam?.trim();
      if (!rawParameterName || NON_QUICKFILL_MAPPING_VALUES.has(rawParameterName)) {
        return;
      }

      splitRevitParameterTokens(rawParameterName).forEach((token) => {
        const normalizedToken = normalizeRevitParameterName(token);
        if (knownParameterNames.has(normalizedToken)) {
          return;
        }

        knownParameterNames.add(normalizedToken);
        mappedParameterNames.add(normalizedToken);
      });
    });
  });

  return Array.from(mappedParameterNames)
    .sort((a, b) => a.localeCompare(b))
    .map((parameterName) => ({
      id: createMappedQuickFillColumnId(parameterName),
      label: parameterName,
      value: '',
      revitParameter: parameterName,
      options: [],
      checked: true,
    }));
}

export function synchronizeQuickFillConfiguredParameters(
  properties: QuickFillProperty[],
  menuConfig: MenuConfiguration | undefined,
  connectorConfig: ConnectorBindingConfiguration | undefined,
  updatePropertyRevitParameter: (id: string, revitParameter: string) => void,
  updatePropertyChecked?: (id: string, checked: boolean) => void
): void {
  const configMapping: Record<string, string> = {
    'skey': menuConfig?.sKeyValue || '',
    'object-type': menuConfig?.objectTypeValue || '',
    'piping-spec': menuConfig?.pipingSpecValue || '',
    'end1': connectorConfig?.endpoint1Value?.value || '',
    'end2': connectorConfig?.endpoint2Value?.value || '',
    'end3': connectorConfig?.endpoint3Value?.value || '',
    'end4': connectorConfig?.endpoint4Value?.value || '',
    'material': connectorConfig?.materialValue?.value || '',
    'design-std': connectorConfig?.designStdValue?.value || '',
    'pressure-class': connectorConfig?.pressureClassValue?.value || '',
    'insulation-spec': connectorConfig?.insulationSpecValue?.value || '',
    'insulation-thickness': connectorConfig?.insulationThicknessValue?.value || '',
    'tracing-spec': connectorConfig?.tracingSpecValue?.value || '',
    'item-description': connectorConfig?.itemDescriptionValue?.value || '',
  };

  properties.forEach((property) => {
    if (!property) return;
    const configValue = configMapping[property.id];
    if (configValue && configValue !== property.revitParameter) {
      updatePropertyRevitParameter(property.id, configValue);
    }
    if (
      configValue
      && REQUIRED_WORKFLOW_PROPERTY_IDS.has(property.id)
      && !property.checked
    ) {
      updatePropertyChecked?.(property.id, true);
    }
  });
}

export function useQuickFillConfigSync() {
  const properties = useQuickFillStore(state => state.properties);
  const updatePropertyChecked = useQuickFillStore(state => state.updatePropertyChecked);
  const updatePropertyRevitParameter = useQuickFillStore(state => state.updatePropertyRevitParameter);
  const upsertExtraProperties = useQuickFillStore(state => state.upsertExtraProperties);
  const removeExtraProperty = useQuickFillStore(state => state.removeExtraProperty);

  const workflowMode = useWorkflowMode();
  const connectorBindingConfig = useConnectorBindingConfiguration();
  const menuConfig = useMenuConfiguration();
  const mappingConfiguration = useConfigurationStore((state) => state.mappingConfiguration);
  const selectedWorkflowParameters = useMemo(
    () => sanitizeRevitParameterNames([
      menuConfig.objectTypeValue,
      menuConfig.sKeyValue,
      menuConfig.pipingSpecValue,
    ]).map((name) => ({ name, selected: true })),
    [menuConfig.objectTypeValue, menuConfig.pipingSpecValue, menuConfig.sKeyValue]
  );

  const configSyncedRef = useRef(false);
  const workflowInitializedRef = useRef(false);
  const autoMappedColumnsRef = useRef<Set<string>>(new Set());

  // Sync properties with configuration from previous pages
  useEffect(() => {
    if (configSyncedRef.current) return;

    const propsArray = Array.isArray(properties) ? properties : [];
    if (propsArray.length === 0) return;

    synchronizeQuickFillConfiguredParameters(
      propsArray,
      menuConfig,
      connectorBindingConfig,
      updatePropertyRevitParameter,
      updatePropertyChecked
    );

    configSyncedRef.current = true;
  }, [
    properties,
    menuConfig,
    connectorBindingConfig,
    updatePropertyChecked,
    updatePropertyRevitParameter,
  ]);

  // Initialize properties from workflow if in multipage mode
  useEffect(() => {
    if (workflowMode === 'multipage' && selectedWorkflowParameters.length > 0 && !workflowInitializedRef.current) {
      selectedWorkflowParameters.forEach(param => {
        const props = Array.isArray(properties) ? properties : [];
        const property = props.find(p => p && (p.label === param.name || p.revitParameter === param.name));
        if (property && !property.checked) {
          updatePropertyChecked(property.id, true);
        }
      });
      workflowInitializedRef.current = true;
    }
  }, [workflowMode, selectedWorkflowParameters, properties, updatePropertyChecked]);

  const extraPropertiesForSync = useQuickFillStore(state => state.extraProperties);

  useEffect(() => {
    const props = Array.isArray(properties) ? properties : [];
    const extras = Array.isArray(extraPropertiesForSync) ? extraPropertiesForSync : [];
    if (props.length === 0) {
      return;
    }

    const desiredMappedColumns = collectMappedQuickFillColumns(mappingConfiguration, props);
    const desiredMappedKeys = new Set(
      desiredMappedColumns
        .map((property) => property.revitParameter)
        .filter((value): value is string => typeof value === 'string')
    );
    const previousMappedKeys = autoMappedColumnsRef.current;
    const mappingSelectionChanged =
      desiredMappedKeys.size !== previousMappedKeys.size
      || Array.from(desiredMappedKeys).some((value) => !previousMappedKeys.has(value));

    if (mappingSelectionChanged && desiredMappedColumns.length > 0) {
      upsertExtraProperties(
        desiredMappedColumns.map((property) => ({
          ...property,
          checked: true,
        }))
      );
    }

    const autoMappedPrefix = 'extra-mapped-';
    const normalizedDesiredKeys = new Set(
      Array.from(desiredMappedKeys).map((key) => key.toLowerCase())
    );
    extras
      .filter((property) => property?.id?.startsWith(autoMappedPrefix))
      .forEach((property) => {
        const normalized = normalizeRevitParameterName(property.revitParameter ?? '').toLowerCase();
        if (!normalizedDesiredKeys.has(normalized)) {
          removeExtraProperty(property.id);
        }
      });

    autoMappedColumnsRef.current = desiredMappedKeys;
  }, [properties, extraPropertiesForSync, mappingConfiguration, upsertExtraProperties, removeExtraProperty]);

  return {
    workflowMode,
    selectedWorkflowParameters,
  };
}
